import { Form, Spin } from "antd";
import AppLoader from "components/AppLoader";
import BaseModal from "components/BaseModal";
import message from "components/CustomMessage";
import { MarkdownFormatter } from "components/MarkupFormatter";
import { MODAL_SIZE_LARGE } from "constants/Layout";
import { marked } from "marked";
import React, { forwardRef, useCallback, useMemo, useState } from "react";
import FindingAPI from "restAPI/FindingAPI";
import { FieldTypes, generateFormFields } from "utils/formUtils";
import { convertCommentToNewFormat } from "./FindingDetails/common";
import { isNil } from "lodash";
import dayjs from "dayjs";
import { getCustomFieldProps, toDayjsFormat } from './utils'
import { getFieldValueFromFormValue } from "hooks/useProposeFlowCustomFields";

const CustomFindingFieldEditModal = forwardRef(
  ({ finding, afterClose, customField }, ref) => {
    const [loading, setLoading] = useState(false);
    const [form] = Form.useForm();

    const isBooleanField = customField.type === "boolean";
    const isLongField = customField.type === "long";
    const isFloatField = customField.type === "float";
    const isDateField = customField.type === "date";
    
    const [initialValues] = useState(() => {
      const rawValue = finding[customField.id];
      let customFieldInitialValue = null;
      if (!isNil(rawValue)) {
        if (isBooleanField) {
          customFieldInitialValue = rawValue === "true";
        } else if (isLongField || isFloatField) {
          customFieldInitialValue = +rawValue;
        } else if (isDateField) {
          const fmt = toDayjsFormat(customField.format || "YYYY-MM-DD");
          const hasTime = /[Hhms]/.test(fmt);
          
          if (hasTime) {
            // Has time: Read the UTC backend value and convert to Local Timezone for the UI
            customFieldInitialValue = dayjs.utc(rawValue).local(); 
          } else {
            // Date only: Parse literally so "2023-10-15" doesn't accidentally become Oct 14th
            customFieldInitialValue = dayjs(rawValue, fmt).isValid()
              ? dayjs(rawValue, fmt)
              : dayjs(rawValue);
          }
        } else {
          const markedResult = rawValue ? marked.parse(rawValue) : null;
          customFieldInitialValue = markedResult ? convertCommentToNewFormat(markedResult) : "";
        }
      }
      return {
        customFieldValue: customFieldInitialValue,
        notes: "",
      };
    });

    const fields = useMemo(() => {
      return [
        {
          name: "summary",
          label: "Summary",
          type: FieldTypes.LABEL,
          value: (
            <MarkdownFormatter markdown={finding.title} collapsible={true} />
          ),
        },
        {
          name: "customFieldValue",
          label: customField.label,
          required: !isBooleanField,
          colSpan: 24,
          ...getCustomFieldProps(customField),
        },
        {
          name: "notes",
          label: "Note",
          type: FieldTypes.TEXT_AREA,
          required: true,
          customLengthRule: {
            max: 1000,
            message: "Maximum length is 1000 characters",
          },
        },
      ];
    }, [finding.title, customField, isBooleanField]);

    const handleOnOk = useCallback(async () => {
      await form.validateFields();
      const data = form.getFieldsValue();

      // convert html to markdown
      const value = data["customFieldValue"];
      data["customFieldValue"] = getFieldValueFromFormValue(value, customField);

      const payload = {
        ...data,
        customFieldId: customField.id,
        customFieldLabel: customField.label,
      };
      setLoading(true);
      await FindingAPI.updateFindingField(finding.id, "custom-fields", payload)
        .promise;
      setLoading(false);
      message.success("Updated Successfully");
    }, [finding, form, customField]);

    const modalProps = {
      title: <>Edit {customField.label}</>,
      onOk: handleOnOk,
      okText: "Submit",
      width: MODAL_SIZE_LARGE,
      className: "modal-full-height no-footer",
    };

    return (
      <BaseModal modalProps={modalProps} ref={ref} afterClose={afterClose}>
        <Spin spinning={loading} indicator={<AppLoader />}>
          <Form layout="vertical" form={form} initialValues={initialValues}>
            {generateFormFields(fields)}
          </Form>
        </Spin>
      </BaseModal>
    );
  },
);

export default CustomFindingFieldEditModal;
